import { F0Icon } from '@factorialco/f0-react'
import { LockLocked } from '@factorialco/f0-react/icons/app'
import { Box, Button, Skeleton, Text } from '@factorialco/deprecated-design-system'
import { type TeamQuery } from 'generated/resources/graphql'

import Popover, {
  type PopoverMenuTouchableOptionProps as Option,
} from 'modules/core/components/PopoverMenu'
import UserAvatar from 'modules/core/components/UserAvatar'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { getEmployeeDisplayName } from 'modules/core/lib/employeeName'
import { handleResult } from 'modules/core/lib/handleMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import useRemoveMember from 'modules/teams/components/Detail/hooks/useRemoveMember'
import app from 'modules/core/store/models/app'
import useDemoteMember from 'modules/teams/components/Detail/hooks/useMemberDemote'

type TeamMembership = NonNullable<NonNullable<TeamQuery['teams']['team']>['memberships']>[number]

interface Props {
  membership: TeamMembership
  teamId: string
  canManageTeams: boolean
  canSeeJobCatalog: boolean
  canSeeJobCatalogTree: boolean
  canSeeJobTitle: boolean
  hasLegacyJobCatalog: boolean
}

const Leader = ({
  membership,
  teamId,
  canManageTeams,
  canSeeJobCatalog,
  canSeeJobCatalogTree,
  canSeeJobTitle,
  hasLegacyJobCatalog,
}: Props) => {
  const { removeMember, loading: removeLoading } = useRemoveMember()
  const { demoteMember, loading: demoteLoading } = useDemoteMember()
  const employee = membership.employee
  const referenceContract = employee.referenceContract
  const jobCatalogLevel = referenceContract?.jobCatalogLevel
  const jobCatalogTreeLevel = referenceContract?.jobCatalogTreeLevel
  const role = jobCatalogLevel?.role

  const getJobPosition = () => {
    if (!canSeeJobTitle) {
      return { isLocked: true, value: '' }
    }

    const canSeeEmployeeJobTitle = referenceContract?._policies?.seeJobTitle?.can ?? false

    if (!canSeeEmployeeJobTitle) {
      return { isLocked: true, value: '' }
    }

    const preferredJobTitle = referenceContract?.jobTitle ?? ''

    if (preferredJobTitle) {
      return { isLocked: false, value: preferredJobTitle }
    }

    if (hasLegacyJobCatalog) {
      if (!canSeeJobCatalog) {
        return { isLocked: true, value: '' }
      }

      if (!role || role.archived) {
        return { isLocked: false, value: '' }
      }

      const levelName = jobCatalogLevel?.isDefault ? '' : (jobCatalogLevel?.name ?? '')
      return {
        isLocked: false,
        value: levelName ? `${role.name} / ${levelName}` : role.name,
      }
    }

    if (!canSeeJobCatalogTree) {
      return { isLocked: true, value: '' }
    }

    if (!jobCatalogTreeLevel?.ancestor?.name || !jobCatalogTreeLevel.name) {
      return { isLocked: false, value: '' }
    }

    return {
      isLocked: false,
      value: `${jobCatalogTreeLevel.ancestor.name} / ${jobCatalogTreeLevel.name}`,
    }
  }

  const jobPosition = getJobPosition()
  const { navigation } = useNavigation()

  const onClickMemberDemote = async () => {
    track(trackingEvents.teams.demoteLeaderStart, {
      team_id: teamId,
      employee_id: employee.id,
    })
    const result = await demoteMember({ id: membership.id })
    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      track(trackingEvents.teams.demoteLeaderSubmit, {
        team_id: teamId,
        employee_id: employee.id,
      })
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.removed_successfully'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.save_error'),
      })
    }
  }

  const onClickMemberRemove = async () => {
    track(trackingEvents.teams.removeMemberStart, {
      team_id: teamId,
      employee_id: employee.id,
      is_lead: true,
    })
    const result = await removeMember({ id: membership.id })
    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      track(trackingEvents.teams.removeMemberSubmit, {
        team_id: teamId,
        employee_id: employee.id,
        is_lead: true,
      })
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.removed_successfully'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.save_error'),
      })
    }
  }

  const options: Option[] = [
    {
      title: i18n.t('teams.change_to_member'),
      onClick: onClickMemberDemote,
    },
    {
      title: i18n.t('teams.leaders.remove_title'),
      onClick: onClickMemberRemove,
      danger: true,
    },
  ]

  return (
    <Box
      padding="s12"
      border={{
        all: {
          style: 'solid',
          width: 's1',
          color: 'grey400',
        },
      }}
      style={{ maxWidth: '100%' }}
      borderRadius={{
        all: 'abs008',
      }}
    >
      {removeLoading || demoteLoading ? (
        <Skeleton width="full" height="s40" />
      ) : (
        <Box width="full" gap="s16" flexDirection="row" justifyContent="spaceBetween">
          <Box justifyContent="flexStart" flexDirection="row" alignItems="center">
            <UserAvatar
              user={{
                fullName: `${employee.firstName} ${employee.lastName}`,
                avatar: employee.avatar?.url ?? '',
              }}
              size="s40"
            />
            <Box paddingLeft="s24" alignItems="flexStart">
              <Text.Normal weight="medium" color="grey1000">
                {getEmployeeDisplayName({
                  fullName: `${employee.firstName} ${employee.lastName}`,
                  lastName: employee.lastName,
                  preferredName: employee.preferredName ?? undefined,
                })}
              </Text.Normal>
              {canSeeJobTitle && (
                <Box paddingTop="s4">
                  {jobPosition.isLocked ? (
                    <F0Icon icon={LockLocked} />
                  ) : (
                    <Text.Normal size="200" color="grey800" weight="medium">
                      {jobPosition.value}
                    </Text.Normal>
                  )}
                </Box>
              )}
            </Box>
          </Box>
          <Box alignItems="center" justifyContent="flexEnd" flexDirection="row" gap="s12">
            {canManageTeams && (
              <Popover type="list">
                <Popover.Trigger>
                  <Button.Decorative type="secondary" iconName="ellipsis" />
                </Popover.Trigger>
                <Popover.Content side="bottom" align="center">
                  {options.map(({ title, description, onClick, danger }, index) => (
                    <Popover.TouchableOption
                      key={index}
                      danger={danger}
                      title={title}
                      onClick={onClick}
                      description={description}
                    />
                  ))}
                </Popover.Content>
              </Popover>
            )}
            <Button.Router
              to={navigation.employees.id.fullPath({ id: employee.id })}
              type="secondary"
              iconName="arrow-right"
            />
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default Leader
